import React from "react";

import { formatMoney } from "utils/admin";

import {
  ActionDivider,
  DashedNotice,
  FieldErrorText,
  FormInput,
  FormSelect,
  FormTextArea,
  MutedPanel,
  PanelAccentText,
  PanelDescriptionText,
  PanelTitleText,
  PrimaryActionButton,
  SecondaryActionButton,
  SectionFormGrid,
  SectionStack,
  TwoColumnFieldGrid,
} from "pages/app/workspace/sections/styles/workspace-section.styles";

type RequestContextFormProps = {
  athleteOwnedAccount: boolean;
  availableTemplates: any[];
  onPlayerChange: (value: string) => void;
  playerError?: string;
  selectableAthletes: any[];
  selectedPlayerId: string;
};

export const RequestContextForm: React.FC<RequestContextFormProps> = ({
  athleteOwnedAccount,
  availableTemplates,
  onPlayerChange,
  playerError,
  selectableAthletes,
  selectedPlayerId,
}) => (
  <SectionStack>
    <FormSelect
      aria-invalid={Boolean(playerError)}
      disabled={athleteOwnedAccount}
      onChange={(event) => onPlayerChange(event.target.value)}
      value={selectedPlayerId}
    >
      <option value="">Select an athlete</option>
      {selectableAthletes.map((athlete: any) => (
        <option key={athlete.id} value={athlete.id}>
          {athlete.full_name}
        </option>
      ))}
    </FormSelect>
    {playerError ? <FieldErrorText>{playerError}</FieldErrorText> : null}

    <TwoColumnFieldGrid>
      {availableTemplates.map((template: any) => (
        <MutedPanel key={template.id}>
          <PanelTitleText>{template.title}</PanelTitleText>
          <PanelDescriptionText>{template.description}</PanelDescriptionText>
          <PanelAccentText>{formatMoney(template.price)}</PanelAccentText>
        </MutedPanel>
      ))}
    </TwoColumnFieldGrid>

    {athleteOwnedAccount ? (
      <DashedNotice>
        The current API exposes request templates and fulfilment controls for your player profile,
        but it does not yet return a dedicated athlete inbox of incoming fan requests.
      </DashedNotice>
    ) : null}
  </SectionStack>
);

type RequestActionsFormProps = {
  athleteOwnedAccount: boolean;
  canCreateRequests: boolean;
  canFulfillRequests: boolean;
  createPending: boolean;
  fulfillPending: boolean;
  onCreate: () => void;
  onFulfill: () => void;
  onRequestDescriptionChange: (value: string) => void;
  onRequestPriceChange: (value: string) => void;
  onRequestTitleChange: (value: string) => void;
  onRequestToFulfillChange: (value: string) => void;
  requestDescription: string;
  requestPriceError?: string;
  requestPrice: string;
  requestTitleError?: string;
  requestTitle: string;
  requestToFulfillError?: string;
  requestToFulfill: string;
  selectedPlayerId: string;
};

export const RequestActionsForm: React.FC<RequestActionsFormProps> = ({
  athleteOwnedAccount,
  canCreateRequests,
  canFulfillRequests,
  createPending,
  fulfillPending,
  onCreate,
  onFulfill,
  onRequestDescriptionChange,
  onRequestPriceChange,
  onRequestTitleChange,
  onRequestToFulfillChange,
  requestDescription,
  requestPriceError,
  requestPrice,
  requestTitleError,
  requestTitle,
  requestToFulfillError,
  requestToFulfill,
  selectedPlayerId,
}) => (
  <SectionFormGrid>
    {canCreateRequests ? (
      <>
        <FormInput
          aria-invalid={Boolean(requestTitleError)}
          onChange={(event) => onRequestTitleChange(event.target.value)}
          placeholder="Request title"
          value={requestTitle}
        />
        {requestTitleError ? <FieldErrorText>{requestTitleError}</FieldErrorText> : null}
        <FormTextArea
          onChange={(event) => onRequestDescriptionChange(event.target.value)}
          placeholder="Request description"
          value={requestDescription}
        />
        <FormInput
          aria-invalid={Boolean(requestPriceError)}
          min="0"
          onChange={(event) => onRequestPriceChange(event.target.value)}
          placeholder="Price"
          type="number"
          value={requestPrice}
        />
        {requestPriceError ? <FieldErrorText>{requestPriceError}</FieldErrorText> : null}
        <PrimaryActionButton
          disabled={!selectedPlayerId || !requestTitle || !requestPrice || createPending}
          onClick={onCreate}
          type="button"
        >
          {createPending ? "Purchasing..." : "Create request"}
        </PrimaryActionButton>
      </>
    ) : athleteOwnedAccount ? (
      <DashedNotice>
        Player accounts cannot create fan purchases from this workspace. Use the control below when
        you need to mark a request as fulfilled for your own athlete profile.
      </DashedNotice>
    ) : (
      <DashedNotice>
        This role can review request activity but cannot create new fan requests from this
        workspace.
      </DashedNotice>
    )}

    {canFulfillRequests ? (
      <>
        <ActionDivider />
        <FormInput
          aria-invalid={Boolean(requestToFulfillError)}
          onChange={(event) => onRequestToFulfillChange(event.target.value)}
          placeholder={
            athleteOwnedAccount ? "Paste a request UUID to fulfil" : "Request UUID to fulfil"
          }
          value={requestToFulfill}
        />
        {requestToFulfillError ? <FieldErrorText>{requestToFulfillError}</FieldErrorText> : null}
        <SecondaryActionButton
          disabled={!requestToFulfill || fulfillPending}
          onClick={onFulfill}
          type="button"
        >
          {fulfillPending ? "Updating..." : "Fulfil request"}
        </SecondaryActionButton>
      </>
    ) : null}
  </SectionFormGrid>
);
